import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import './StorageSamplesPanel.css';

import { useStorage } from '../../../hook/useStorage';
import { useSample } from '../../../hook/useSample';
import { CardProvider } from '../../../hoc/CardProvider';
import WorkspacePanel, { WorkspacePanelHead } from '../../../components/WorkspacePanel/WorkspacePanel';
import StorageInfo from '../../../components/StorageInfo/StorageInfo';
import SamplesList from '../../../components/SamplesList/SamplesList';
import { sortType } from '../../../components/SamplesList/SampleListContent';
import AddSampleModal from '../../../components/Modals/AddSampleModal/AddSampleModal';
import ConfirmModal from '../../../components/Modals/ConfirmModal/ConfirmModal';
import SimpleButton, { ButtonTypes } from '../../../components/SimpleComponents/SimpleButton';

/**
 * List of samples for active storage
 * @component
 */
const StorageSamplesPanel = ({ isOwned, onActivateSample, onUpdateSample }) => {
	const { activeStorage } = useStorage();
	const { samples, activeSample, updatedSample, deleteSample } = useSample();

	const [collectionInfo, setCollectionInfo] = useState(true);

	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [isAddModalOpen, setAddModalOpen] = useState(false);

	const handleShowAddSample = useCallback(() => {
		setAddModalOpen(true);
	}, []);

	useEffect(() => {
		const handleKeyDown = (event) => {
			switch (event.key) {
				case 'Insert':
					if (isOwned) handleShowAddSample();
					return;
				case 'Escape':
					onActivateSample(null);
					return;
				case 'Delete':
					if (activeSample && isOwned && !isAddModalOpen) {
						setDeleteModalOpen(true);
					}
					return;
				default:
					return;
			}
		};

		document.addEventListener('keydown', handleKeyDown, false);
		return () => {
			document.removeEventListener('keydown', handleKeyDown, false);
		};
	}, [onActivateSample, activeSample, isOwned, isAddModalOpen, handleShowAddSample]);

	return (
		<WorkspacePanel className="storage-samples">
			<WorkspacePanelHead title={activeStorage.name}>
				<span className="action" onClick={toggleInfo}>
					{collectionInfo ? '(hide info)' : '(show info)'}
				</span>
				{isOwned ? (
					<SimpleButton
						title="Add a sample to this storage (Ins)"
						type={ButtonTypes.DEFAULT}
						value="Add Sample"
						onClick={handleShowAddSample}
					/>
				) : null}
			</WorkspacePanelHead>

			{collectionInfo ? <StorageInfo storage={activeStorage} /> : null}

			<SamplesList
				active_sample={activeSample}
				default_sort={sortType.collectorNumber}
				isOwned={isOwned}
				samples={samples}
				showStorage={false}
				updated_sample={updatedSample}
				onActivate={onActivateSample}
			/>

			<CardProvider>
				<AddSampleModal
					isOpen={isAddModalOpen}
					storage={activeStorage}
					onAddSample={handleAddSample}
					onRequestClose={handleCloseAddSample}
				/>
			</CardProvider>

			<ConfirmModal
				defaultYes={true}
				isOpen={isDeleteModalOpen}
				title="Delete sample"
				onResult={handleDeleteSample}
			>
				Are you sure you want to delete this sample?
			</ConfirmModal>
		</WorkspacePanel>
	);

	function toggleInfo() {
		setCollectionInfo(!collectionInfo);
	}

	function handleCloseAddSample() {
		setAddModalOpen(false);
	}

	function handleAddSample(sample) {
		onUpdateSample(sample);
		setAddModalOpen(false);
	}

	function handleDeleteSample(result) {
		if (result) {
			if (activeSample) {
				deleteSample(activeSample.id, (status) => {
					if (status) {
						onActivateSample(null);
						onUpdateSample(null);
					}
				});
			}
		}
		setDeleteModalOpen(false);
	}
};

StorageSamplesPanel.propTypes = {
	// Is this storage owned by current user
	isOwned: PropTypes.bool.isRequired,
	// Callback function on sample select
	onActivateSample: PropTypes.func.isRequired,
	// Callback function after sample updated
	onUpdateSample: PropTypes.func.isRequired,
};

export default StorageSamplesPanel;
